import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  BellOff,
  BellRing,
  History,
  List,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { RootState, useAppDispatch } from '../store';
import {
  fetchAlertRules,
  fetchAlertEvents,
  fetchAlertStatus,
  fetchTelegramStatus,
  fetchWhatsAppStatus,
  createAlertRule,
  editAlertRule,
  deleteAlertRule,
  toggleAlertRule,
  fetchBuyActionWatchlist,
  createBuyActionWatch,
  deleteBuyActionWatch,
} from '../store/thunks/alertsThunks';
import { fetchFeatureStatus as fetchAutoScreenFeatureStatus } from '../store/slices/autoScreenSlice';
import {
  MATouchAlertRule,
  MATouchAlertRulePayload,
  MATouchAlertScopeMode,
  MATouchAlertEvent,
  TelegramAlertStatus,
  MATouchAlertDigestMode,
  BuyActionWatch,
} from '../types';
import { resolveEntryStatus } from '../lib/deepAnalysis/entryStatusDisplay';
import { isMATouchAlertsEnabled } from './layouts/navigationConfig';
import AutoScreenSettingsCard from './AutoScreenSettingsCard';
import AlertsConnectionsTab from './AlertsConnectionsTab';
import AlertsQuickStart from './AlertsQuickStart';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/Tabs';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/Select';
import { Textarea } from './ui/Textarea';
import { cn } from '../lib/utils';
import './AlertsTab.css';

type AlertFormField = 'name' | 'ma_selection' | 'score_threshold';
type AlertFormErrors = Partial<Record<AlertFormField, string>>;
type AlertsInnerTab = 'rules' | 'watchlist' | 'connections' | 'autoscreen';

const QUICK_START_DISMISSED_KEY = 'alerts.quickStart.dismissed';
const QUICK_START_TESTED_KEY = 'alerts.quickStart.tested';
const QUICK_START_EMAIL_ACK_KEY = 'alerts.quickStart.emailAck';

// Some browsers (Safari incognito, Firefox first-party isolation) throw on
// localStorage access. Wrap reads/writes so we degrade to in-memory state
// instead of crashing the component tree on mount.
const readLocalFlag = (key: string): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};
const writeLocalFlag = (key: string, value: boolean) => {
  try {
    if (typeof window === 'undefined') return;
    if (value) window.localStorage.setItem(key, 'true');
    else window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable - keep in-memory only.
  }
};

const AlertsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { rules, events, runStatus, loading, error, whatsappStatus } = useSelector(
    (state: RootState) =>
      state.alerts || {
        rules: [],
        events: [],
        runStatus: null,
        loading: false,
        error: null,
        whatsappStatus: null,
      }
  );
  // Buy-Action watchlist (PLAN_BUY_ACTION_ALERT_2026-06-20). Selected
  // separately so the union-default object above keeps its existing shape.
  const watchEntries = useSelector(
    (state: RootState) => state.alerts?.watchEntries ?? []
  );
  // P1-1: read auto-screen feature flag so we can hide the tab trigger when
  // the backend has the feature disabled (`/api/auto-screen/feature-status`
  // → `enabled: false`). Without this guard the tab renders an empty panel.
  const autoScreenFeatureEnabled = useSelector(
    (state: RootState) => state.autoScreen?.featureEnabled ?? null
  );

  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  const [activeTab, setActiveTab] = useState<AlertsInnerTab>('rules');
  const [quickStartDismissed, setQuickStartDismissed] = useState(() => readLocalFlag(QUICK_START_DISMISSED_KEY));
  const [quickStartTested, setQuickStartTested] = useState(() => readLocalFlag(QUICK_START_TESTED_KEY));
  // P2-2: explicit email-as-channel acknowledgment. Email is auto-bound to
  // login email but we deliberately don't auto-tick step 1 on that alone (per
  // plan). Users opt in by clicking "Use Email for alerts" on the Email card.
  const [emailAcknowledged, setEmailAcknowledged] = useState(() => readLocalFlag(QUICK_START_EMAIL_ACK_KEY));
  // Telegram status lives only in local state today (Redux follow-up). Fetched
  // at AlertsTab mount so QuickStart `hasUsableChannel` is correct on first
  // paint regardless of which tab the user lands on.
  const [telegramStatus, setTelegramStatus] = useState<TelegramAlertStatus | null>(null);
  const [channelState, setChannelState] = useState({ telegramConnected: false, whatsappConnected: false });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRule, setEditingRule] = useState<MATouchAlertRule | null>(null);
  const [formData, setFormData] = useState<MATouchAlertRulePayload>({
    name: '',
    ma_selection: [],
    scope_mode: 'ALL_STOCKS',
    score_threshold: 5.0,
    email_enabled: true,
    telegram_enabled: false,
    whatsapp_enabled: false,
    email_digest_mode: 'PER_RUN_DIGEST',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<AlertFormErrors>({});

  // Buy-Action watchlist add-form local state (ticker + channel toggles).
  const [watchTicker, setWatchTicker] = useState('');
  const [watchChannels, setWatchChannels] = useState({
    email_enabled: true,
    telegram_enabled: false,
    whatsapp_enabled: false,
  });
  const [watchSubmitting, setWatchSubmitting] = useState(false);
  const [watchError, setWatchError] = useState<string | null>(null);

  // Check if feature is enabled
  const featureEnabled = isMATouchAlertsEnabled();

  useEffect(() => {
    if (!featureEnabled) return;
    dispatch(fetchAlertRules(true));
    dispatch(fetchAlertEvents());
    dispatch(fetchAlertStatus());
    // Buy-Action watchlist for the Watchlist sub-tab.
    dispatch(fetchBuyActionWatchlist());
    // Fetch channel status at AlertsTab mount so QuickStart's `hasUsableChannel`
    // reflects reality even when the user lands on the Rules tab and never
    // opens Connections.
    dispatch(fetchWhatsAppStatus()).catch(() => undefined);
    // P1-1: fetch auto-screen feature flag at AlertsTab mount so we know
    // whether to render the Auto-Screen TabsTrigger from the start. The
    // AutoScreenSettingsCard also dispatches this, but it only mounts when
    // the autoscreen tab is active - too late to decide trigger visibility.
    dispatch(fetchAutoScreenFeatureStatus());
    (async () => {
      try {
        const status = await dispatch(fetchTelegramStatus() as any);
        if (isMountedRef.current) setTelegramStatus(status as TelegramAlertStatus);
      } catch {
        // Status fetch failed - leave telegramStatus null; QuickStart treats
        // this as "not connected" which is the safe default.
      }
    })();
  }, [dispatch, featureEnabled]);

  // P1-1 follow-up: if user is on Auto-Screen but the feature flips off,
  // snap back to Rules so they don't sit on a hidden tab. Hook MUST live
  // before the early return below to satisfy rules-of-hooks.
  useEffect(() => {
    if (autoScreenFeatureEnabled === false && activeTab === 'autoscreen') {
      setActiveTab('rules');
    }
  }, [autoScreenFeatureEnabled, activeTab]);

  if (!featureEnabled) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-surface-container-high rounded-xl p-8 text-center">
          <BellOff size={48} className="mx-auto mb-4 text-text-muted" aria-hidden="true" />
          <h4 className="text-text-primary mb-2">MA Touch Alerts</h4>
          <p className="text-text-muted">
            This feature is currently disabled. Enable it in your environment settings.
          </p>
        </div>
      </div>
    );
  }

  const resetForm = () => {
    setFormData({
      name: '',
      ma_selection: [],
      scope_mode: 'ALL_STOCKS',
      score_threshold: 5.0,
      email_enabled: true,
      telegram_enabled: false,
      whatsapp_enabled: false,
      email_digest_mode: 'PER_RUN_DIGEST',
      notes: '',
    });
    setFormError(null);
    setFieldErrors({});
    setEditingRule(null);
    setShowCreateForm(false);
  };

  const safeWatchEntries = (Array.isArray(watchEntries) ? watchEntries : []).filter(
    (w): w is BuyActionWatch => Boolean(w && typeof w === 'object' && w.ticker)
  );

  const watchNoChannel =
    !watchChannels.email_enabled &&
    !watchChannels.telegram_enabled &&
    !watchChannels.whatsapp_enabled;

  const handleAddWatch = async () => {
    const ticker = watchTicker.trim().toUpperCase();
    setWatchError(null);
    if (!ticker) {
      setWatchError(t('alerts.buyAction.errors.tickerRequired', { defaultValue: 'Enter a ticker.' }));
      return;
    }
    if (watchNoChannel) {
      setWatchError(t('alerts.buyAction.noChannel', { defaultValue: 'Select at least one channel.' }));
      return;
    }
    setWatchSubmitting(true);
    try {
      await dispatch(
        createBuyActionWatch({
          ticker,
          email_enabled: watchChannels.email_enabled,
          telegram_enabled: watchChannels.telegram_enabled,
          whatsapp_enabled: watchChannels.whatsapp_enabled,
        })
      );
      setWatchTicker('');
    } catch (err: any) {
      setWatchError(
        err?.message ||
          t('alerts.buyAction.errors.addFailed', { defaultValue: 'Failed to add watch.' })
      );
    } finally {
      setWatchSubmitting(false);
    }
  };

  const handleRemoveWatch = async (ticker: string) => {
    try {
      await dispatch(deleteBuyActionWatch(ticker));
    } catch {
      // Error surfaced via shared alerts slice.
    }
  };

  const safeRules = (Array.isArray(rules) ? rules : []).filter(
    (rule): rule is MATouchAlertRule => Boolean(rule && typeof rule === 'object' && rule.id)
  );

  const safeEvents = (Array.isArray(events) ? events : []).filter(
    (event): event is MATouchAlertEvent =>
      Boolean(event && typeof event === 'object' && (event.id || event.id === 0))
  );

  const hasUsableChannel =
    channelState.telegramConnected ||
    channelState.whatsappConnected ||
    Boolean(telegramStatus?.connected) ||
    Boolean(whatsappStatus?.connected) ||
    emailAcknowledged;
  const quickStartComplete = hasUsableChannel && safeRules.length > 0 && quickStartTested;
  const showQuickStart = !quickStartDismissed && !quickStartComplete;

  const validateForm = (): AlertFormErrors => {
    const nextErrors: AlertFormErrors = {};

    if (!formData.name?.trim()) {
      nextErrors.name = 'Rule name is required.';
    }

    if (!formData.ma_selection || formData.ma_selection.length === 0) {
      nextErrors.ma_selection = 'Select at least one moving average.';
    }

    if (formData.scope_mode === 'SCORE_ABOVE_THRESHOLD') {
      if (
        formData.score_threshold === undefined ||
        Number.isNaN(formData.score_threshold) ||
        formData.score_threshold < 0 ||
        formData.score_threshold > 10
      ) {
        nextErrors.score_threshold = 'Score threshold must be between 0 and 10.';
      }
    }

    return nextErrors;
  };

  const clearFieldError = (field: AlertFormField) => {
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const nextErrors = validateForm();
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormError('Please fix the highlighted fields.');
      return;
    }

    try {
      if (editingRule) {
        await dispatch(editAlertRule(editingRule.id, formData));
      } else {
        await dispatch(createAlertRule(formData));
      }
      // Guard against tab navigation during the in-flight request.
      if (!isMountedRef.current) return;
      resetForm();
      dispatch(fetchAlertRules(true));
    } catch (err: any) {
      if (!isMountedRef.current) return;
      setFormError(err?.message || 'Failed to save rule');
    }
  };

  const handleEdit = (rule: MATouchAlertRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      ma_selection: rule.ma_selection,
      scope_mode: rule.scope_mode,
      score_threshold: rule.score_threshold,
      email_enabled: rule.email_enabled,
      telegram_enabled: Boolean(rule.telegram_enabled),
      whatsapp_enabled: Boolean(rule.whatsapp_enabled),
      email_digest_mode: rule.email_digest_mode,
      notes: rule.notes || '',
    });
    setShowCreateForm(true);
    setFieldErrors({});
  };

  const handleDelete = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this alert rule?')) {
      try {
        await dispatch(deleteAlertRule(ruleId));
      } catch (err) {
        console.error('Failed to delete rule:', err);
      }
    }
  };

  const handleToggle = async (rule: MATouchAlertRule) => {
    try {
      await dispatch(toggleAlertRule(rule.id, !rule.enabled));
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    }
  };

  const openCreateRuleModal = () => {
    setEditingRule(null);
    setFieldErrors({});
    setFormError(null);
    setShowCreateForm(true);
  };

  const handleQuickStartTestSuccess = () => {
    writeLocalFlag(QUICK_START_TESTED_KEY, true);
    setQuickStartTested(true);
  };

  const handleDismissQuickStart = () => {
    writeLocalFlag(QUICK_START_DISMISSED_KEY, true);
    setQuickStartDismissed(true);
  };

  const handleShowQuickStart = () => {
    writeLocalFlag(QUICK_START_DISMISSED_KEY, false);
    setQuickStartDismissed(false);
  };

  // P2-2: called when user clicks "Use Email for alerts" on the Email card.
  // Persists the flag and ticks QuickStart step 1.
  const handleAcknowledgeEmail = () => {
    writeLocalFlag(QUICK_START_EMAIL_ACK_KEY, true);
    setEmailAcknowledged(true);
  };

  const maOptions = ['MA_50', 'MA_100', 'MA_150', 'MA_200'];

  const handleMaToggle = (ma: string) => {
    const current = formData.ma_selection || [];
    if (current.includes(ma)) {
      setFormData({ ...formData, ma_selection: current.filter(m => m !== ma) });
    } else {
      setFormData({ ...formData, ma_selection: [...current, ma] });
    }
    if (fieldErrors.ma_selection) {
      clearFieldError('ma_selection');
    }
  };

  // Latest-run badge variant
  const latestRunBadgeVariant: 'bullish' | 'bearish' | 'default' = (() => {
    const status = runStatus?.latest_run?.status;
    if (status === 'completed') return 'bullish';
    if (status === 'failed') return 'bearish';
    return 'default';
  })();

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as AlertsInnerTab)}
      className="contents"
    >
      {/*
        Single TabsContent per value (P1 fix), per-panel overflow (P2 fix).
        Layout:
          - Top region (header + QuickStart + TabsList) is shrink-0; never scrolls.
          - Auto-Screen / Connections panels: flex-1 overflow-y-auto - natural scroll.
          - Rules panel: flex-1 flex-col with internal bounded scrollers per card.
      */}
      {/*
        Outer alerts container. `overflow-x-hidden` prevents horizontal page
        scroll. `overflow-y-auto` lets the page scroll vertically if total
        content exceeds the viewport on smaller laptops (1280×720, 1366×768).
        Inner card scrollers (alerts-rules-scroll, alerts-events-scroll) keep
        their own internal overflow for long lists. Below: a min-height is
        applied to the rules row so cards always render at a usable size
        even when the row is starved of vertical space.
      */}
      <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col overflow-x-hidden overflow-y-auto px-0 py-0.5 pb-20 md:px-4 md:py-6 md:pb-4">
        <div className="shrink-0 space-y-2 px-0 md:space-y-3" data-testid="alerts-header-region">
          <header className="px-3 md:text-center">
            <h2 className="flex items-center justify-center gap-2.5 text-lg font-bold text-text-primary md:text-2xl">
              <span className="inline-flex items-center justify-center rounded-xl bg-accent-primary/10 p-1.5 text-accent-primary md:p-2">
                <Bell size={20} aria-hidden="true" />
              </span>
              <span className="md:hidden">Alerts</span>
              <span className="hidden md:inline">MA Touch Alerts</span>
            </h2>
            <p className="mt-0.5 text-xs text-text-muted md:mt-1 md:text-base">
              Create notification rules for moving average touches
            </p>
          </header>

          {showQuickStart ? (
            <AlertsQuickStart
              rulesCount={safeRules.length}
              hasUsableChannel={hasUsableChannel}
              testCompleted={quickStartTested}
              onGoToConnections={() => setActiveTab('connections')}
              onAddRule={() => {
                setActiveTab('rules');
                openCreateRuleModal();
              }}
              onTestChannel={() => setActiveTab('connections')}
              onDismiss={handleDismissQuickStart}
            />
          ) : (
            <div className="px-3 md:px-0">
              <Button
                variant="link"
                size="sm"
                onClick={handleShowQuickStart}
                className="min-h-[44px]"
              >
                {t('alerts.quickStart.show')}
              </Button>
            </div>
          )}

          <div className="px-3 md:px-0">
            {/*
              Underline tab style - these are inner sub-tabs WITHIN the Alerts
              area (Rules / Connections / Auto-Screen), not top-level nav.
              `data-testid="alerts-tablist"` (P3-2) lets smoke tests scope tab
              queries to this list (the global page has its own [role=tablist]
              for primary nav).
            */}
            <TabsList
              data-testid="alerts-tablist"
              className="scrollbar-none h-auto w-full max-w-full justify-start gap-1 overflow-x-auto overflow-y-hidden rounded-none border-0 border-b border-border-subtle bg-transparent p-0"
            >
              <TabsTrigger
                value="rules"
                className="-mb-px min-h-[44px] rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-text-muted shadow-none hover:bg-transparent hover:text-text-secondary data-[state=active]:border-accent-primary data-[state=active]:bg-transparent data-[state=active]:text-text-primary data-[state=active]:shadow-none"
              >
                {t('alerts.tabs.rules')}
              </TabsTrigger>
              <TabsTrigger
                value="watchlist"
                className="-mb-px min-h-[44px] rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-text-muted shadow-none hover:bg-transparent hover:text-text-secondary data-[state=active]:border-accent-primary data-[state=active]:bg-transparent data-[state=active]:text-text-primary data-[state=active]:shadow-none"
              >
                {t('alerts.tabs.watchlist', { defaultValue: 'Watchlist' })}
              </TabsTrigger>
              <TabsTrigger
                value="connections"
                className="-mb-px min-h-[44px] rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-text-muted shadow-none hover:bg-transparent hover:text-text-secondary data-[state=active]:border-accent-primary data-[state=active]:bg-transparent data-[state=active]:text-text-primary data-[state=active]:shadow-none"
              >
                {t('alerts.tabs.connections')}
              </TabsTrigger>
              {/* P1-1: hide Auto-Screen trigger when the backend feature flag
                  is off. Keep the trigger visible while flag is `null` (still
                  loading) - the card itself shows a loading skeleton. */}
              {autoScreenFeatureEnabled !== false && (
                <TabsTrigger
                  value="autoscreen"
                  className="-mb-px min-h-[44px] rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-text-muted shadow-none hover:bg-transparent hover:text-text-secondary data-[state=active]:border-accent-primary data-[state=active]:bg-transparent data-[state=active]:text-text-primary data-[state=active]:shadow-none"
                >
                  {t('alerts.tabs.autoScreen')}
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>

        {/* Auto-Screen panel - natural height; the outer container's
            overflow-y-auto handles scrolling at the page level. No inner
            scroller (matches Rules tab pattern). */}
        <TabsContent value="autoscreen" className="mt-2 px-3 md:mt-3 md:px-0">
          <AutoScreenSettingsCard />
        </TabsContent>

        {/* Buy-Action Watchlist panel (PLAN_BUY_ACTION_ALERT_2026-06-20).
            One-shot buy-zone watches: a watch fires once when the ticker flips
            into a buy-action status, then auto-deletes (R6 re-arm note below). */}
        <TabsContent
          value="watchlist"
          className="mt-2 flex min-h-0 flex-1 flex-col gap-3 px-3 md:mt-3 md:px-0"
        >
          {/* Add-ticker form */}
          <div className="rounded-xl bg-surface-container-high p-3 md:p-4">
            <h3 className="mb-1 text-base font-semibold text-text-primary">
              <BellRing className="mr-2 inline text-accent-primary" size={16} aria-hidden="true" />
              {t('alerts.buyAction.addTitle', { defaultValue: 'Watch a stock for a Buy signal' })}
            </h3>
            <p className="mb-3 text-xs leading-snug text-text-secondary">
              {t('alerts.buyAction.addHint', {
                defaultValue:
                  'Get a one-shot alert the moment a stock currently in the buy zone flips to a Buy / Strong Buy.',
              })}
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Label htmlFor="buy-action-add-ticker" className="mb-1 block text-sm font-medium text-text-primary">
                  {t('alerts.buyAction.tickerLabel', { defaultValue: 'Ticker' })}
                </Label>
                <Input
                  id="buy-action-add-ticker"
                  type="text"
                  value={watchTicker}
                  onChange={(e) => setWatchTicker(e.target.value.toUpperCase())}
                  placeholder={t('alerts.buyAction.tickerPlaceholder', { defaultValue: 'e.g. ticker symbol' })}
                  maxLength={10}
                  className="uppercase"
                />
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={handleAddWatch}
                disabled={watchSubmitting || watchNoChannel || !watchTicker.trim()}
                leftIcon={
                  watchSubmitting ? (
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Plus size={16} aria-hidden="true" />
                  )
                }
                className="sm:w-auto"
              >
                {t('alerts.buyAction.addButton', { defaultValue: 'Add Watch' })}
              </Button>
            </div>

            {/* Channel toggles */}
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              <label htmlFor="buy-action-list-email" className="flex items-center gap-2 cursor-pointer">
                <input
                  id="buy-action-list-email"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                  checked={watchChannels.email_enabled}
                  onChange={(e) => setWatchChannels((c) => ({ ...c, email_enabled: e.target.checked }))}
                />
                <span className="text-sm text-text-primary">
                  {t('alerts.buyAction.channels.email', { defaultValue: 'Email' })}
                </span>
              </label>
              <label htmlFor="buy-action-list-telegram" className="flex items-center gap-2 cursor-pointer">
                <input
                  id="buy-action-list-telegram"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                  checked={watchChannels.telegram_enabled}
                  onChange={(e) => setWatchChannels((c) => ({ ...c, telegram_enabled: e.target.checked }))}
                />
                <span className="text-sm text-text-primary">
                  {t('alerts.buyAction.channels.telegram', { defaultValue: 'Telegram' })}
                </span>
              </label>
              <label htmlFor="buy-action-list-whatsapp" className="flex items-center gap-2 cursor-pointer">
                <input
                  id="buy-action-list-whatsapp"
                  type="checkbox"
                  className="h-4 w-4 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                  checked={watchChannels.whatsapp_enabled}
                  onChange={(e) => setWatchChannels((c) => ({ ...c, whatsapp_enabled: e.target.checked }))}
                />
                <span className="text-sm text-text-primary">
                  {t('alerts.buyAction.channels.whatsapp', { defaultValue: 'WhatsApp' })}
                </span>
              </label>
            </div>

            {(watchError || watchNoChannel) && (
              <p className="mt-2 text-xs text-accent-warning" role="alert">
                {watchError ||
                  t('alerts.buyAction.noChannel', { defaultValue: 'Select at least one channel.' })}
              </p>
            )}

            {/* R6 re-arm note */}
            <p className="mt-3 rounded-lg bg-surface-container-low p-2 text-xs leading-snug text-text-secondary">
              {t('alerts.buyAction.reArmNote', {
                defaultValue:
                  'A watch is removed automatically when it fires - re-add it to watch again.',
              })}
            </p>
          </div>

          {/* Watch list */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-surface-container-high">
            <h3 className="shrink-0 border-b border-border-subtle px-4 pb-3 pt-4 text-base font-semibold text-text-primary">
              <List className="mr-2 inline text-accent-primary" size={16} aria-hidden="true" />
              {t('alerts.buyAction.listTitle', { defaultValue: 'Watched Stocks' })} ({safeWatchEntries.length})
            </h3>
            <div className="scrollbar-thin min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-4 [scrollbar-gutter:stable]">
              {safeWatchEntries.length === 0 && (
                <div className="py-8 text-center">
                  <BellRing className="mx-auto mb-3 text-text-muted" size={32} aria-hidden="true" />
                  <p className="text-text-muted">
                    {t('alerts.buyAction.empty', {
                      defaultValue:
                        'No watched stocks yet. Add a ticker above, or use "Alert on Buy" from a chart.',
                    })}
                  </p>
                </div>
              )}
              {safeWatchEntries.map((watch) => {
                const status = resolveEntryStatus(watch.baseline_entry_status);
                return (
                  <div
                    key={watch.ticker}
                    className="flex items-center justify-between gap-2 border-t border-border-subtle py-2.5 first:border-t-0 md:py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-text-primary">{watch.ticker}</span>
                        <span className={cn('rounded px-1.5 py-0.5 text-xs', status.badgeClass)}>
                          {status.emoji ? `${status.emoji} ` : ''}
                          {t(`alerts.buyAction.status.${status.code}`, { defaultValue: status.label })}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {watch.email_enabled && (
                          <Badge variant="primary" size="sm" showDot>
                            {t('alerts.buyAction.channels.email', { defaultValue: 'Email' })}
                          </Badge>
                        )}
                        {watch.telegram_enabled && (
                          <Badge
                            size="sm"
                            showDot
                            // eslint-disable-next-line no-restricted-syntax -- Telegram brand color is intentional and theme-agnostic.
                            className="bg-[#229ED9]/20 text-[#64b5f6]"
                          >
                            {t('alerts.buyAction.channels.telegram', { defaultValue: 'Telegram' })}
                          </Badge>
                        )}
                        {watch.whatsapp_enabled && (
                          <Badge variant="bullish" size="sm" showDot>
                            {t('alerts.buyAction.channels.whatsapp', { defaultValue: 'WhatsApp' })}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="min-h-[44px] shrink-0 text-accent-bearish hover:bg-accent-bearish/10 hover:text-accent-bearish"
                      onClick={() => handleRemoveWatch(watch.ticker)}
                      aria-label={t('alerts.buyAction.removeWatch', { defaultValue: 'Remove buy alert' })}
                      leftIcon={<Trash2 size={16} aria-hidden="true" />}
                    >
                      <span className="hidden sm:inline">
                        {t('alerts.buyAction.remove', { defaultValue: 'Remove' })}
                      </span>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Connections panel - same single-scroller pattern. */}
        <TabsContent value="connections" className="mt-2 mx-3 pb-1 md:mt-3 md:mx-0">
          <AlertsConnectionsTab
            onTestSuccess={handleQuickStartTestSuccess}
            onChannelStatusChange={setChannelState}
            emailAcknowledged={emailAcknowledged}
            onAcknowledgeEmail={handleAcknowledgeEmail}
          />
        </TabsContent>

        {/* Rules panel - single TabsContent (was duplicated, fixed 2026-05-04) */}
        <TabsContent
          value="rules"
          className="mt-2 flex min-h-0 flex-1 flex-col gap-2 px-0 md:mt-3 md:gap-3"
        >
          {/* Add Rule Button */}
          <div className="px-3 md:flex md:justify-end md:px-0">
            <Button
              variant="primary"
              size="md"
              fullWidth
              leftIcon={<Plus size={16} aria-hidden="true" />}
              onClick={openCreateRuleModal}
              className="md:w-auto"
            >
              <span className="md:hidden">Add Alert</span>
              <span className="hidden md:inline">Add Rule</span>
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <div
              className="mx-3 mb-0 rounded-xl border border-accent-bearish/40 bg-accent-bearish/10 p-3 text-accent-bearish md:mx-0 md:p-4"
              role="alert"
            >
              <AlertCircle className="mr-2 inline" size={16} aria-hidden="true" />
              {error}
            </div>
          )}

          {runStatus?.ma_touch_alerts_enabled === false && (
            <div
              className="mx-3 rounded-xl border border-accent-warning/40 bg-accent-warning/10 p-2 text-xs text-text-primary md:mx-0 md:p-3 md:text-sm"
              role="status"
            >
              <AlertTriangle className="mr-1.5 inline text-accent-warning" size={14} aria-hidden="true" />
              <strong>Alerts service is disabled.</strong> Rules you create will not fire and no
              emails will be sent until an administrator enables the service
              (<code className="px-1">MA_TOUCH_ALERTS_ENABLED</code>) on the server.
            </div>
          )}

          {/* Status Summary */}
          {runStatus && (
            <div className="grid grid-cols-3 gap-1.5 px-3 md:gap-2 md:px-0">
              <div className="flex flex-col justify-center rounded-xl bg-surface-container-high p-2 text-center md:p-3">
                <div className="text-xs uppercase tracking-wider text-text-secondary md:text-sm">
                  Latest Run
                </div>
                <div className="mt-0.5 flex justify-center md:mt-1">
                  {runStatus.latest_run ? (
                    <Badge variant={latestRunBadgeVariant} size="sm" showDot>
                      {runStatus.latest_run.status}
                    </Badge>
                  ) : (
                    <span className="text-xs text-text-muted">No runs</span>
                  )}
                </div>
              </div>
              <div
                className="flex flex-col justify-center rounded-xl bg-surface-container-high p-2 text-center md:p-3"
                title="Events inserted in the most recent run"
              >
                <div className="text-xs uppercase tracking-wider text-text-secondary md:text-sm">
                  New (Last Run)
                </div>
                <div className="mt-0.5 text-xl font-bold tabular-nums text-text-primary md:mt-1 md:text-2xl">
                  {runStatus.latest_run?.events_inserted || 0}
                </div>
              </div>
              <div className="flex flex-col justify-center rounded-xl bg-surface-container-high p-2 text-center md:p-3">
                <div className="text-xs uppercase tracking-wider text-text-secondary md:text-sm">
                  Pending
                </div>
                <div className="mt-0.5 text-xl font-bold tabular-nums text-text-primary md:mt-1 md:text-2xl">
                  {runStatus.pending_deliveries}
                </div>
              </div>
            </div>
          )}

          {/* Rules + events row */}
          <div
            data-testid="alerts-rules-row"
            className="mx-3 mt-1 flex min-h-[420px] flex-1 flex-col gap-3 max-md:min-h-[min(48svh,420px)] md:mx-0 md:mt-2 md:min-h-[440px] md:flex-row md:gap-4"
          >
            {/* Rules List */}
            <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden rounded-xl bg-surface-container-high max-md:min-h-[min(34svh,220px)]">
              <h3 className="shrink-0 border-b border-border-subtle px-4 pb-3 pt-4 text-base font-semibold text-text-primary">
                <List className="mr-2 inline text-accent-primary" size={16} aria-hidden="true" />
                Alert Rules ({safeRules.length})
              </h3>
              <div
                className="scrollbar-thin min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-4 [scrollbar-gutter:stable]"
                data-testid="alerts-rules-scroll"
              >
                {loading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-accent-primary" size={24} aria-hidden="true" />
                  </div>
                )}
                {!loading && safeRules.length === 0 && (
                  <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-container text-text-muted">
                      <BellOff size={26} aria-hidden="true" />
                    </span>
                    <p className="max-w-[16rem] text-sm leading-relaxed text-text-muted">
                      No alert rules yet. Use{' '}
                      <span className="font-medium text-text-secondary">Add Rule</span> to get
                      notified when a stock touches a moving average.
                    </p>
                  </div>
                )}
                {safeRules.map((rule: MATouchAlertRule) => (
                  <div
                    key={rule.id}
                    className="border-t border-border-subtle py-2.5 first:border-t-0 md:py-3"
                  >
                    <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="mb-0.5 text-sm font-semibold leading-snug text-text-primary">
                          {rule.name || 'Untitled rule'}
                        </h4>
                        <p className="mb-1.5 text-xs leading-snug text-text-muted">
                          {rule.ma_selection?.join(', ') || 'No MA selected'} | {rule.scope_mode || 'ALL_STOCKS'}
                          {rule.scope_mode === 'SCORE_ABOVE_THRESHOLD' && ` (>=${rule.score_threshold})`}
                        </p>
                        {/* Channel state chips. Single-line at md+ via flex-nowrap;
                            mobile keeps flex-wrap so cramped widths still work.
                            Labels dropped "On/Off" suffix - the colored dot
                            (showDot) conveys state, freeing horizontal width. */}
                        <div className="flex flex-wrap gap-1.5 md:flex-nowrap">
                          <Badge variant={rule.enabled ? 'bullish' : 'default'} size="sm" showDot>
                            {rule.enabled ? 'Active' : 'Disabled'}
                          </Badge>
                          <Badge
                            variant={rule.email_enabled ? 'primary' : 'default'}
                            size="sm"
                            showDot
                          >
                            Email
                          </Badge>
                          <Badge
                            size="sm"
                            showDot
                            className={
                              rule.telegram_enabled
                                ? // eslint-disable-next-line no-restricted-syntax -- Telegram brand color (#229ED9/#64b5f6) is intentional and theme-agnostic.
                                  'bg-[#229ED9]/20 text-[#64b5f6]'
                                : 'bg-surface-container text-text-muted'
                            }
                          >
                            Telegram
                          </Badge>
                          <Badge
                            variant={rule.whatsapp_enabled ? 'bullish' : 'default'}
                            size="sm"
                            showDot
                          >
                            WhatsApp
                          </Badge>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-1.5 sm:gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => handleToggle(rule)}
                        >
                          {rule.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => handleEdit(rule)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-h-[44px] text-accent-bearish hover:bg-accent-bearish/10 hover:text-accent-bearish"
                          onClick={() => handleDelete(rule.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Events */}
            <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-hidden rounded-xl bg-surface-container-high max-md:min-h-[min(34svh,220px)]">
              <h3 className="shrink-0 border-b border-border-subtle px-3 pb-3 pt-3 text-base font-semibold text-text-primary md:px-4 md:pb-3 md:pt-4 md:text-lg">
                <History className="mr-2 inline text-accent-primary" size={16} aria-hidden="true" />
                Recent Events ({safeEvents.length})
              </h3>
              <div
                className="scrollbar-thin min-h-0 flex-1 overflow-auto overscroll-contain px-3 pb-3 md:px-4 md:pb-4 [scrollbar-gutter:stable]"
                data-testid="alerts-events-scroll"
              >
                {safeEvents.length === 0 && (
                  <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-container text-text-muted">
                      <History size={26} aria-hidden="true" />
                    </span>
                    <p className="max-w-[17rem] text-sm leading-relaxed text-text-muted">
                      No events yet. When one of your rules fires, the matched touches show up
                      here.
                    </p>
                  </div>
                )}
                {safeEvents.length > 0 && (
                  <div className="min-w-0 overflow-x-auto">
                    <table className="w-full min-w-[480px] text-sm [&_tbody_tr:nth-child(even)]:bg-surface-container-low">
                      <thead className="sticky top-0 z-[1] bg-surface-container">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-text-secondary">Date</th>
                          <th className="px-3 py-2 text-left font-medium text-text-secondary">Ticker</th>
                          <th className="px-3 py-2 text-left font-medium text-text-secondary">MA</th>
                          <th className="px-3 py-2 text-left font-medium text-text-secondary">Price Range</th>
                          <th className="px-3 py-2 text-left font-medium text-text-secondary">MA Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeEvents.map((event: MATouchAlertEvent) => {
                          const toNum = (v: unknown): number | null => {
                            if (typeof v === 'number' && Number.isFinite(v)) return v;
                            if (typeof v === 'string' && v.trim() !== '') {
                              const n = Number(v);
                              return Number.isFinite(n) ? n : null;
                            }
                            return null;
                          };
                          const lo = toNum(event.touch_price_low);
                          const hi = toNum(event.touch_price_high);
                          const ma = toNum(event.ma_value);
                          return (
                            <tr
                              key={event.id}
                              className="border-t border-border-subtle hover:bg-surface-container"
                            >
                              <td className="px-3 py-2 text-text-primary">{event.touch_date}</td>
                              <td className="px-3 py-2 font-medium text-text-primary">{event.ticker}</td>
                              <td className="px-3 py-2 text-text-primary">{event.ma_name}</td>
                              <td className="px-3 py-2 text-text-primary">
                                {lo !== null ? `$${lo.toFixed(2)}` : 'N/A'} -{' '}
                                {hi !== null ? `$${hi.toFixed(2)}` : 'N/A'}
                              </td>
                              <td className="px-3 py-2 text-text-primary">
                                {ma !== null ? `$${ma.toFixed(2)}` : 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </div>

      {/* Create/Edit Modal - outside scroll container; fixed backdrop unaffected by overflow.
          max-h inline (instead of CSS) so we don't need to edit AlertsTab.css this PR. */}
      {showCreateForm && (
        <div className="alerts-rule-modal-backdrop" role="presentation" onClick={resetForm}>
          <div
            className="alerts-rule-modal max-h-[90svh] supports-[height:100dvh]:max-h-[90dvh] overflow-y-auto bg-surface-container-high border border-border-subtle rounded-xl shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b border-border-subtle p-4">
              <h5 className="m-0 text-text-primary">
                <Bell className="mr-2 inline text-accent-primary" size={16} aria-hidden="true" />
                {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
              </h5>
              <Button
                variant="secondary"
                size="sm"
                className="min-h-[44px]"
                onClick={resetForm}
                aria-label="Close"
                leftIcon={<X size={16} aria-hidden="true" />}
              >
                Close
              </Button>
            </div>
            <div className="p-4 md:p-6">
              <form onSubmit={handleSubmit} noValidate>
                {formError && (
                  <div
                    className="mb-4 p-3 bg-accent-bearish/10 border border-accent-bearish/40 rounded-lg text-accent-bearish text-sm"
                    role="alert"
                  >
                    <AlertCircle className="mr-2 inline" size={14} aria-hidden="true" />
                    {formError}
                  </div>
                )}

                <div className="mb-3">
                  <Label htmlFor="ma-rule-name" className="mb-1 block font-semibold text-text-primary">
                    Rule Name <span className="text-accent-bearish">*</span>
                  </Label>
                  <Input
                    id="ma-rule-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (fieldErrors.name) {
                        clearFieldError('name');
                      }
                    }}
                    placeholder="e.g., Watchlist MA Alerts"
                    maxLength={120}
                    error={Boolean(fieldErrors.name)}
                    errorMessage={fieldErrors.name ?? ''}
                  />
                </div>

                <div className="mb-3">
                  {/* Not a Label primitive - the controls below are <Button>s,
                      not a single <input>; an orphan <label> would be semantically
                      misleading (screen readers announce it as a form-field label). */}
                  <p className="mb-1 block text-sm font-semibold text-text-primary">
                    Moving Averages <span className="text-accent-bearish">*</span>
                  </p>
                  <p className="text-text-muted mb-2 text-xs">Select one or more values to monitor.</p>
                  <div className="flex gap-2 flex-wrap">
                    {maOptions.map((ma) => {
                      const selected = formData.ma_selection?.includes(ma);
                      return (
                        <Button
                          key={ma}
                          type="button"
                          variant={selected ? 'primary' : 'secondary'}
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => handleMaToggle(ma)}
                        >
                          {ma.replace('_', ' ')}
                        </Button>
                      );
                    })}
                  </div>
                  {fieldErrors.ma_selection && (
                    <div className="mt-1 text-xs text-accent-bearish">{fieldErrors.ma_selection}</div>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="ma-scope-mode" className="mb-1 block font-semibold text-text-primary">
                    Stock Scope
                  </Label>
                  <Select
                    value={formData.scope_mode ?? 'ALL_STOCKS'}
                    onValueChange={(v) =>
                      setFormData({ ...formData, scope_mode: v as MATouchAlertScopeMode })
                    }
                  >
                    <SelectTrigger id="ma-scope-mode" aria-label="Stock scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL_STOCKS">All Stocks</SelectItem>
                      <SelectItem value="SCORE_ABOVE_THRESHOLD">Score Above Threshold</SelectItem>
                      <SelectItem value="FAVORITES_ONLY">My Favorites Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.scope_mode === 'SCORE_ABOVE_THRESHOLD' && (
                  <div className="mb-3">
                    <Label
                      htmlFor="ma-score-threshold"
                      className="mb-1 block font-semibold text-text-primary"
                    >
                      Minimum Score (0-10) <span className="text-accent-bearish">*</span>
                    </Label>
                    <Input
                      id="ma-score-threshold"
                      type="number"
                      aria-label="Minimum score threshold"
                      value={formData.score_threshold ?? 0}
                      onChange={(e) => {
                        // parseFloat('') is NaN; coerce empty/invalid to 0.
                        const parsed = parseFloat(e.target.value);
                        setFormData({
                          ...formData,
                          score_threshold: Number.isFinite(parsed) ? parsed : 0,
                        });
                        if (fieldErrors.score_threshold) {
                          clearFieldError('score_threshold');
                        }
                      }}
                      min={0}
                      max={10}
                      step={0.5}
                      error={Boolean(fieldErrors.score_threshold)}
                      errorMessage={fieldErrors.score_threshold ?? ''}
                    />
                  </div>
                )}

                {/* Modal channel checkboxes - id + name + htmlFor pairing
                    closes the DevTools "form field should have id or name"
                    issue. The wrapping <label htmlFor={id}> matches the
                    input's id so the click target covers icon + text. */}
                <div className="mb-3">
                  <label htmlFor="alerts-rule-email" className="flex items-start gap-2 cursor-pointer">
                    <input
                      id="alerts-rule-email"
                      name="email_enabled"
                      type="checkbox"
                      className="mt-0.5 h-5 w-5 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                      checked={formData.email_enabled}
                      onChange={(e) =>
                        setFormData({ ...formData, email_enabled: e.target.checked })
                      }
                    />
                    <span className="block text-sm font-medium text-text-primary">
                      Enable Email Notifications
                    </span>
                  </label>
                </div>

                <div className="mb-3">
                  <label htmlFor="alerts-rule-telegram" className="flex items-start gap-2 cursor-pointer">
                    <input
                      id="alerts-rule-telegram"
                      name="telegram_enabled"
                      type="checkbox"
                      className="mt-0.5 h-5 w-5 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                      checked={Boolean(formData.telegram_enabled)}
                      onChange={(e) =>
                        setFormData({ ...formData, telegram_enabled: e.target.checked })
                      }
                    />
                    <span>
                      <span className="block text-sm font-medium text-text-primary">
                        Enable Telegram Notifications
                      </span>
                      <span className="mt-0.5 block text-xs text-text-secondary">
                        Requires a connected Telegram chat in the Connections tab.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="mb-3">
                  <label htmlFor="alerts-rule-whatsapp" className="flex items-start gap-2 cursor-pointer">
                    <input
                      id="alerts-rule-whatsapp"
                      name="whatsapp_enabled"
                      type="checkbox"
                      className="mt-0.5 h-5 w-5 rounded border-border-default bg-surface-container-lowest text-accent-primary focus:ring-2 focus:ring-accent-primary focus:ring-offset-0"
                      checked={Boolean(formData.whatsapp_enabled)}
                      onChange={(e) =>
                        setFormData({ ...formData, whatsapp_enabled: e.target.checked })
                      }
                    />
                    <span>
                      <span className="block text-sm font-medium text-text-primary">
                        {t('alerts.ruleChannels.whatsapp.label')}
                      </span>
                      <span className="mt-0.5 block text-xs text-text-secondary">
                        {t('alerts.ruleChannels.whatsapp.requires')}
                      </span>
                    </span>
                  </label>
                </div>

                {formData.email_enabled && (
                  <div className="mb-3">
                    <Label
                      htmlFor="ma-email-mode"
                      className="mb-1 block font-semibold text-text-primary"
                    >
                      Email Mode
                    </Label>
                    <Select
                      value={formData.email_digest_mode ?? 'PER_RUN_DIGEST'}
                      onValueChange={(v) =>
                        setFormData({ ...formData, email_digest_mode: v as MATouchAlertDigestMode })
                      }
                    >
                      <SelectTrigger id="ma-email-mode" aria-label="Email notification mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PER_RUN_DIGEST">Per Run Digest</SelectItem>
                        <SelectItem value="PER_EVENT">Per Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="mb-4">
                  <Label htmlFor="ma-alert-notes" className="mb-1 block font-semibold text-text-primary">
                    Notes (optional)
                  </Label>
                  <Textarea
                    id="ma-alert-notes"
                    aria-label="Alert rule notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button type="submit" variant="primary" size="md" disabled={loading}>
                    {loading ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                  </Button>
                  <Button type="button" variant="secondary" size="md" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Tabs>
  );
};

export default AlertsTab;
